/**
 * Users Tab Component
 *
 * User management interface for admins
 * Features: List users, create, update, block/unblock, archive, delete
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  listUsers,
  createUser,
  blockUser,
  unblockUser,
  archiveUser,
  deleteUser,
} from '@/services/adminApi';
import type { User, UserCreateRequest, UserRole, UserStatus } from '@/types/user';

export default function UsersTab() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');

  // Form state for creating user
  const [newUser, setNewUser] = useState<UserCreateRequest>({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'user' as UserRole,
  });

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listUsers(filterStatus || undefined, filterRole || undefined);
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [filterStatus, filterRole]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createUser(newUser);
      setShowCreateForm(false);
      setNewUser({ username: '', password: '', full_name: '', email: '', role: 'user' as UserRole });
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleBlockUser = async (userId: number) => {
    if (confirm('Are you sure you want to block this user?')) {
      try {
        await blockUser(userId);
        fetchUsers();
      } catch (err: any) {
        alert(err.response?.data?.detail || 'Failed to block user');
      }
    }
  };

  const handleUnblockUser = async (userId: number) => {
    try {
      await unblockUser(userId);
      fetchUsers();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unblock user');
    }
  };

  const handleArchiveUser = async (userId: number) => {
    if (confirm('Are you sure you want to archive this user?')) {
      try {
        await archiveUser(userId);
        fetchUsers();
      } catch (err: any) {
        alert(err.response?.data?.detail || 'Failed to archive user');
      }
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (confirm('Are you sure you want to PERMANENTLY delete this user? This cannot be undone!')) {
      try {
        await deleteUser(userId);
        fetchUsers();
      } catch (err: any) {
        alert(err.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const getStatusBadge = (status: UserStatus) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      blocked: 'bg-red-100 text-red-800',
      archived: 'bg-gray-100 text-gray-800',
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${colors[status] || 'bg-gray-100 text-gray-800'}`;
  };

  const getRoleBadge = (role: UserRole) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      user: 'bg-blue-100 text-blue-800',
    };
    return `px-2 py-1 text-xs font-medium rounded-full ${colors[role] || 'bg-gray-100 text-gray-800'}`;
  };

  /**
   * Check if a user is protected from dangerous operations
   * Protected users: root admin (username="admin") and current logged-in user
   */
  const isProtectedUser = (user: User): boolean => {
    // Protect root admin account
    if (user.username === 'admin') {
      return true;
    }
    // Protect current logged-in user (prevent self-harm)
    if (currentUser && user.username === currentUser.username) {
      return true;
    }
    return false;
  };

  return (
    <div>
      {/* Header with Actions */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex gap-4">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 bg-black text-white rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            {showCreateForm ? '✕ Cancel' : '+ Create User'}
          </button>

          {/* Filters */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
        </div>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="mb-6 bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">Create New User</h3>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Username"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="password"
                placeholder="Password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <input
                type="email"
                placeholder="Email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                required
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value as UserRole })}
                className="px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create User
            </button>
          </form>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading users...</p>
        </div>
      )}

      {/* Users Table */}
      {!isLoading && users.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                      <div className="text-sm text-gray-500">@{user.username}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getRoleBadge(user.role)}>{user.role}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={getStatusBadge(user.status)}>{user.status}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex justify-end gap-2">
                      {isProtectedUser(user) ? (
                        <span className="text-xs text-gray-400 italic">
                          {user.username === 'admin' ? 'Root Admin' : 'Current User'}
                        </span>
                      ) : (
                        <>
                          {user.status === 'active' && (
                            <button
                              onClick={() => handleBlockUser(user.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Block
                            </button>
                          )}
                          {user.status === 'blocked' && (
                            <button
                              onClick={() => handleUnblockUser(user.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Unblock
                            </button>
                          )}
                          <button
                            onClick={() => handleArchiveUser(user.id)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Archive
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && users.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No users found</p>
        </div>
      )}
    </div>
  );
}
